import { createTheme, useTheme, type Theme, type TypographyVariant } from "@mui/material/styles";
import { useMemo } from "react";
import { compactPaneWidth } from "work/_views/work-pane-layout";

/**
 * Type is the one thing on these pages that a container query cannot narrow.
 *
 * Everything else about the compact treatment is CSS the pane can drive — `work-pane-layout`. Text
 * is not, because Material sizes every variant in `rem` and `rem` resolves against the document root:
 * a subtree cannot change what a `rem` is worth inside it, so a 34px page title stays 34px in a 608px
 * pane exactly as it is on a 1920px display. That is the half of #4964 the container queries left
 * behind — the layout reflowed and the words in it did not.
 *
 * Material's own answer to this is a nested theme, and that is what this is: the same variants at a
 * smaller size, handed to the page through a `ThemeProvider` so every `Typography`, `Button`, table
 * cell and form label below it picks the scaled value up without being told. Keyed on the pane's
 * measured width rather than the window's, for the reason the rest of the pane layout is.
 */

/** The variants a nested theme is allowed to restate. Anything Material derives from these follows. */
const SCALED_VARIANTS: readonly TypographyVariant[] = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2",
  "button",
  "caption",
  "overline",
];

/**
 * Three steps, and deliberately not the same steps the layout takes.
 *
 * The layout has one threshold — `md`, where the page stops being able to seat its controls at full
 * size — and hanging the type on that too would make a pane one pixel either side of it change by a
 * whole step. Type does not work that way: it is too big or too small by degrees, and a pane at 926px
 * wants slightly tighter text long before it wants a different layout. So this ramps from `lg` down
 * while the layout still switches at `md`.
 *
 * The values are modest for the same reason. Text that shrinks faster than the boxes around it buys
 * width the page does not need and costs legibility it does: at 0.875 the body copy is 14px, a size
 * this application already uses elsewhere, and the page title comes down from 34px to 30px — enough
 * that the title, its breadcrumb and the header's buttons stop competing for one row.
 */
export function workPaneFontScale(theme: Theme, paneWidth: number | undefined): number {
  if (paneWidth === undefined || paneWidth >= theme.breakpoints.values.lg) return 1;
  if (paneWidth >= compactPaneWidth(theme)) return 0.9375;
  return paneWidth >= theme.breakpoints.values.sm ? 0.875 : 0.8125;
}

/** Rounded because emotion writes the value out verbatim and `1.8593749999999998rem` helps nobody. */
function scaleFontSize(fontSize: unknown, scale: number): unknown {
  if (typeof fontSize === "number") return Math.round(fontSize * scale * 1000) / 1000;
  if (typeof fontSize !== "string") return fontSize;
  const parsed = /^(-?[\d.]+)(rem|em|px)$/.exec(fontSize.trim());
  if (!parsed) return fontSize;
  return `${Math.round(parseFloat(parsed[1]) * scale * 1000) / 1000}${parsed[2]}`;
}

export function scaleWorkPaneTypography(theme: Theme, scale: number): Theme {
  if (scale === 1) return theme;

  const typography: Record<string, unknown> = {};
  SCALED_VARIANTS.forEach((variant) => {
    const style = theme.typography[variant];
    if (!style) return;
    typography[variant] = { ...style, fontSize: scaleFontSize(style.fontSize, scale) };
  });

  return createTheme(theme, { typography });
}

/**
 * The theme a work page hands its own content.
 *
 * Memoized on the scale rather than on the width deliberately: the width changes on every frame of a
 * drag and the scale changes three times in total, so a theme is built when the pane crosses a
 * threshold instead of sixty times a second. That is a stability guarantee the page's identity depends
 * on — a fresh theme object every frame would remount nothing but would re-render everything — not
 * defensive memoization.
 */
export default function useWorkPaneTheme(paneWidth: number | undefined): Theme {
  const theme = useTheme();
  const scale = workPaneFontScale(theme, paneWidth);
  return useMemo(() => scaleWorkPaneTypography(theme, scale), [theme, scale]);
}
